import { atomWithMutation, queryClientAtom } from 'jotai-tanstack-query';
import { R as GeneralResources } from '@/Resources.resx';
import { R } from '@/features/programs/resources/ProgramsResources.resx';
import {
    UpdateSundvaegtProgram,
    NnSundVaegtProgramDto,
} from '@/shared/interfaces/api/sundvaegt-interfaces';
import { updateNnSundVaegtProgram } from '../api/updateNnSundVaegtProgram';
import { fetchNnSundvaegtProgramAtom } from './fetchNnSundvaegtProgramAtom';

const queryKey = ['nnsundvaegt-weightprogram'] as const;

export const updateNnSundVaegtProgramAtom = atomWithMutation<
    string, // TData (return type)
    Omit<UpdateSundvaegtProgram, 'Id'>, // TVariables (you pass DesiredWeightKg only)
    Error, // TError
    { previous?: NnSundVaegtProgramDto } // TContext (used for rollback)
>((get) => {
    const queryClient = get(queryClientAtom);

    return {
        mutationFn: async (variables: Omit<UpdateSundvaegtProgram, 'Id'>) => {
            // read the freshest program right before calling the API
            const program = get(fetchNnSundvaegtProgramAtom).data;
            if (!program) {
                throw new Error('No SundVægt program loaded');
            }
            try {
                return await updateNnSundVaegtProgram({
                    Id: program.Id,
                    ...variables,
                });
            } catch (error) {
                throw new Error(
                    `${R.SundvaegtSetWeightGoalErrorMessage}: ${
                        error instanceof Error
                            ? error.message
                            : GeneralResources.UnknownError
                    }`,
                );
            }
        },

        // optimistic update
        onMutate: async (variables: Omit<UpdateSundvaegtProgram, 'Id'>) => {
            await queryClient.cancelQueries({ queryKey });

            const previous =
                queryClient.getQueryData<NnSundVaegtProgramDto>(queryKey);

            if (previous) {
                queryClient.setQueryData<NnSundVaegtProgramDto>(queryKey, {
                    ...previous,
                    DesiredWeightKg:
                        variables.DesiredWeightKg ?? previous.DesiredWeightKg,
                });
            }

            return { previous };
        },

        // rollback on error
        onError: (_err, _vars, ctx) => {
            if (ctx?.previous) {
                queryClient.setQueryData<NnSundVaegtProgramDto>(
                    queryKey,
                    ctx.previous,
                );
            }
        },

        // ensure server truth after success
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey });
        },
    };
});
